import PropTypes from 'prop-types';
import { Component } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import styled from 'styled-components';
import {
  Badge,
  Box,
  Icon,
  IconName,
  Text,
  Token,
  VersionIdBadge,
} from '@usealloy/component-library';

import EllipsisMenu from '~/application-review/EllipsisMenu';
import { Field } from '~/application-review/Fields';
import { WORKFLOW_TYPES } from '~/constants';
import DocumentListModal from '~/documents/DocumentListModal';
import { DocumentReviewModalV2 } from '~/documents/DocumentReview_v2/DocumentReviewModal';
import { DocumentUploadModal } from '~/documents/DocumentUploadModal';
import { EVENT_TYPE_EVALUATION_MAPPING, IMPROVED_EVENT_EVALS_MAPPING } from '~/entities/evaluations/constants';
import EvaluationHeaderResult from '~/entities/evaluations/EvaluationHeaderResult';
import RawResponseModal from '~/entities/evaluations/RawResponseModal';
import RerunEvaluationModal from '~/entities/evaluations/RerunEvaluationModal';
import { StatusOutcome } from '~/entities/evaluations/StatusOutcome';
import TagRuleModal from '~/entities/evaluations/TagRuleModal';
import { formatTimestamp, snakeToCaps } from '~/helpers/formatting';
import { permissionsMixin } from '~/helpers/mixins';
import withDashboardHooks from '~/helpers/WithDashboardHooksHOC';
import NotesModal from '~/notes/NotesModal';

export const testIdMinusCollapseIcon = 'evaluation-header-minus-collapse-icon';

const Header = styled.div`
  display: flex;
  flex-wrap: nowrap;
  gap: var(--cl-space-2);
  overflow: hidden;
  align-items: center;
`;

const HeaderText = styled(Text)`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  font-size: var(--cl-font-size-5) !important;
  color: var(--cl-text-default);
`;

const EvaluationHeaderPropTypes = {
  addSnackBar: PropTypes.func.isRequired,
  entity: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.object,
  ]),
  evaluation: PropTypes.object.isRequired,
  applications: PropTypes.object,
  toggleExpanded: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.func,
  ]),
  groupId: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.string,
  ]),
  documentTypes: PropTypes.arrayOf(PropTypes.shape({
    document_type_id: PropTypes.number,
    type: PropTypes.string,
  })).isRequired,
  apiUrl: PropTypes.string,
  isSwitchWorkflowEnabled: PropTypes.bool,
  doDisplayEvalDocsAndNotes: PropTypes.bool,
  featureFlags: PropTypes.object,
  isInformational: PropTypes.bool,
  isWorkflowVersionActive: PropTypes.bool,
  onDocumentsUpdate: PropTypes.func.isRequired,
};

const EvaluationHeaderDefaultProps = {
  entity: false,
  toggleExpanded: false,
  groupId: false,
  apiUrl: null,
  isSwitchWorkflowEnabled: true,
  featureFlags: {},
  isInformational: false,
};

class ApplicationEvaluationHeader extends permissionsMixin(Component) {
  constructor(props) {
    super(props);
    this.state = {
      isDocumentUploadModalOpen: false,
      isDocumentListModalOpen: false,
      isNotesModalOpen: false,
      isRuleExplainabilityModalOpen: false,
    };

    [
      'createEllipsisMenuProps',
      'handleToggleModal',
      'openNotesModal',
      'renderEvaluationNotesLink',
    ].forEach(method => { this[method] = this[method].bind(this); });
  }

  componentDidMount() {
    const { evaluation } = this.props;

    if (evaluation.status === 'webhook_pending' && evaluation.webhook_pending_services.length) {
      const { addSnackBar } = this.props;
      const joinedServices = evaluation.webhook_pending_services.join(', ');
      addSnackBar({
        title: `Waiting on notification from services (${joinedServices})`,
        type: 'info',
      });
    }
  }

  handleToggleModal(event) {
    this.setState({ modalOpen: this.state.modalOpen ? false : event.target.dataset.modal });
  }

  openNotesModal() {
    this.setState(currentState => ({
      ...currentState,
      isNotesModalOpen: true,
    }));
  }

  renderEvaluationNotesLink(evaluation) {
    return (
      <a className="secondary inline notes-link" onClick={this.openNotesModal}>
        {`Notes (${evaluation.notes.length})`}
      </a>
    );
  }

  createEllipsisMenuProps() {
    const { evaluation, featureFlags } = this.props;
    const options = [];
    const hasEditJourneyAppPermission = this.checkPermission('UPDATE:JourneyApplications:journey_application_id');
    const canRerunEvaluation = (
      this.checkPermission('CREATE:Evaluations:null')
      && hasEditJourneyAppPermission
      && evaluation.final_response !== null
      && evaluation.evaluation_type !== WORKFLOW_TYPES.RECURRING
    );

    if (canRerunEvaluation) {
      options.push({ label: 'Rerun Evaluation', onClick: this.handleToggleModal, dataModal: 'rerun' });
    }

    if (this.checkPermission('CREATE:Notes:null') && hasEditJourneyAppPermission) {
      options.push({ label: 'Add Evaluation Note', onClick: this.openNotesModal });
    }

    if (this.checkPermission('CREATE:Documents:null') && hasEditJourneyAppPermission && evaluation.final_response !== null) {
      options.push({ label: 'Upload Documents', onClick: () => this.setState({ isDocumentUploadModalOpen: true }) });
    }

    if (evaluation.final_response_display !== null) {
      options.push({ label: 'Raw Response', onClick: this.handleToggleModal, dataModal: 'raw-response' });
    }

    if (featureFlags.ruleExplainabilityPhase >= 4) {
      options.push({ label: 'Evaluation Explainability', onClick: () => this.setState({ isRuleExplainabilityModalOpen: true }) });
    }

    return { options };
  }

  getEvaluationLabel() {
    const { evaluation } = this.props;

    const shouldUseEventTypeLabel = Object.keys({
      ...EVENT_TYPE_EVALUATION_MAPPING,
      ...IMPROVED_EVENT_EVALS_MAPPING,
    })
      .includes(evaluation.event_request_type);

    if (shouldUseEventTypeLabel) {
      return snakeToCaps(evaluation.event_request_type);
    }

    switch (evaluation.evaluation_type) {
      case 'transaction':
        return 'Transaction';
      case 'journey_router':
        return 'Router Evaluation';
      case 'portfolio':
        return 'Portfolio Evaluation';
      default:
        return 'Evaluation';
    }
  }

  render() {
    const {
      modalOpen,
      isDocumentListModalOpen,
      isRuleExplainabilityModalOpen,
    } = this.state;
    const {
      applications,
      documentTypes,
      entity,
      evaluation,
      evaluationDocuments,
      groupId,
      isSwitchWorkflowEnabled,
      toggleExpanded,
      onAddNote,
      onRerunEvaluation,
      doPreventReload,
      userContext,
      featureFlags,
      isInformational,
      isWorkflowVersionActive,
      onDocumentsUpdate,
    } = this.props;

    const evaluationType = this.getEvaluationLabel();

    const showNewDocReviewModal = featureFlags.updatedDocumentReviewModal;

    return (
      <div className="evaluation-header">
        <div className="flex full-space no-wrap">
          <Header>
            {isInformational ? (
              <Icon icon={IconName.VialCircleCheck} />
            ) : null}
            <HeaderText size={5}>
              {`${isInformational ? 'Test ' : ''}${evaluationType}`}
            </HeaderText>
            <Token
              compact
              displayContent={
                evaluation.evaluation_token || evaluation.portfolio_evaluation_token
              }
            />
            {evaluation.sandbox
              && <Badge color="black" status="warning">Sandbox</Badge>}
            {Boolean((evaluationDocuments || []).length) && (
              <a
                className="secondary inline"
                onClick={() => this.setState({ isDocumentListModalOpen: true })}
              >
                Documents ({evaluationDocuments.length})
              </a>
            )}
            {Boolean((evaluation.notes || []).length) && (
              this.renderEvaluationNotesLink(evaluation)
            )}
            {toggleExpanded
              && (
              <span
                data-testid={testIdMinusCollapseIcon}
                onClick={toggleExpanded(evaluation.evaluation_token)}
                role="button"
                tabIndex="0"
              >
                <Icon icon={IconName.Minus} />
              </span>
              )}
          </Header>
          {evaluation.evaluation_type !== 'portfolio' && (
            <EllipsisMenu {...this.createEllipsisMenuProps()} data-testid="application-evaluation-header-ellipsis-header" />
          )}
        </div>

        <div className="row">
          <Box direction="row" justifyContent="flex-start" gap={2} style={{ paddingLeft: 'var(--cl-space-2)' }}>
            <div>
              <StatusOutcome evaluation={evaluation} />
            </div>
            {evaluation.result
              && <EvaluationHeaderResult result={evaluation.result} />}
            <Field fieldName="Workflow">
              <a
                href={evaluation.application_url}
                className="secondary inline"
              >
                {isWorkflowVersionActive !== undefined ? (
                  evaluation.application_name
                ) : (
                  `${evaluation.application_name} v.${evaluation.public_version}`
                )}
              </a>
            </Field>
            {isWorkflowVersionActive !== undefined ? (
              <Field fieldName="Version">
                <VersionIdBadge versionId={evaluation.public_version} variant={isWorkflowVersionActive ? 'active' : 'inactive'} />
              </Field>
            ) : null}
            <Field fieldName="Run">
              <div className="grey">
                {evaluation?.timestamp ? formatTimestamp(evaluation.timestamp) : '-'}
              </div>
            </Field>
          </Box>
        </div>
        {evaluation.final_response && (
          <RerunEvaluationModal
            doPreventReload={doPreventReload}
            doPassOriginalEvaluationToken
            entity={entity}
            evaluation={evaluation}
            applications={applications}
            isOpen={['rerun', 'rerun-child'].includes(modalOpen)}
            toggleModal={this.handleToggleModal}
            isChildRun={modalOpen === 'rerun-child'}
            apiUrl={this.props.apiUrl || userContext.typhonDomain}
            groupId={groupId}
            documentTypes={documentTypes}
            isSwitchWorkflowEnabled={isSwitchWorkflowEnabled}
            groupEntityTokenWithWhichToRerunEvaluation={
              this.props.groupEntityTokenWithWhichToRerunEvaluation
            }
            onRerunEvaluation={onRerunEvaluation}
            permissions={this.permissions}
          />
        )}
        <NotesModal
          addingNoteTo={{
            table: 'Evaluations',
            key: 'evaluation_id',
            row_id: evaluation.evaluation_id,
            title: evaluation.evaluation_token,
            token: evaluation.evaluation_token,
            entity_token: entity ? entity.entity_token : null,
          }}
          isNotesModalOpen={this.state.isNotesModalOpen}
          notes={evaluation.notes}
          onAddNote={onAddNote}
          onError={onAddNote}
          updateState={updater => { this.setState(updater(this.state)); }}
        />
        {evaluation.final_response_display && (
          <RawResponseModal
            response={evaluation.final_response_display}
            isOpen={modalOpen === 'raw-response'}
            toggleModal={this.handleToggleModal}
          />
        )}
        {isDocumentListModalOpen && (
          showNewDocReviewModal ? (
            <DocumentReviewModalV2
              documents={evaluationDocuments}
              onClose={() => this.setState({ isDocumentListModalOpen: false })}
              onDocumentsUpdate={onDocumentsUpdate}
            />
          )
            : (
              <DocumentListModal
                documents={evaluationDocuments}
                isOpen
                toggleModal={() => this.setState({ isDocumentListModalOpen: false })}
                entityToken={entity.entity_token}
              />
            )
        )}
        {this.state.isDocumentUploadModalOpen && (
          <DndProvider backend={HTML5Backend}>
            <DocumentUploadModal
              documentTypes={documentTypes}
              entities={{ [entity.entity_token]: entity }}
              evaluationToken={evaluation.evaluation_token}
              isOpen={this.state.isDocumentUploadModalOpen}
              onClose={() => this.setState(() => ({
                isDocumentUploadModalOpen: false,
              }))}
            />
          </DndProvider>
        )}
        {featureFlags?.ruleExplainabilityPhase >= 4 && isRuleExplainabilityModalOpen && (
        <TagRuleModal
          closeModal={() => { this.setState({ isRuleExplainabilityModalOpen: false }); }}
          entityToken={evaluation.entity_token}
          evaluationTags={evaluation.tags}
          evaluationToken={evaluation.evaluation_token}
          workflowToken={evaluation.application_token}
          workflowVersion={evaluation.public_version}
        />
        )}
      </div>
    );
  }
}
ApplicationEvaluationHeader.propTypes = EvaluationHeaderPropTypes;
ApplicationEvaluationHeader.defaultProps = EvaluationHeaderDefaultProps;

export default withDashboardHooks(ApplicationEvaluationHeader);
